import { ChunkContext, ExecutionContext } from '../../../interfaces/chunk/chunk-context.interface';
import { AppStateService } from '../../app-state/app-state.service';
import { PyodideFileJournal } from '../journal/python-journal.helper';

/**
 * Result of context change detection
 */
export interface ContextChangeResult {
  any: boolean;
  needsFullReload: boolean;
  needsProjectFilesReload: boolean;
  needsWorkflowFilesReload: boolean;
}

/**
 * PythonContextManager
 *
 * Manages workflow/project context changes and state tracking.
 * Extracted from PythonService to reduce monolithic service size.
 *
 * Responsibilities:
 * - Track current context state (project, workflow, version, chunk)
 * - Detect context changes
 * - Handle context transitions with cleanup and cache invalidation
 */
export class PythonContextManager {
  // Context state tracking
  /**
   * Last known projectId from AppState.
   * Used ONLY for change detection - compares with fresh value from AppState.
   * Updated after each checkAndHandleContextChange() call.
   *
   * IMPORTANT: This is NOT the source of truth.
   * Always read from AppStateService for current values.
   * Use getCurrentProjectId() getter to access this tracked value.
   */
  private currentProjectId?: number;

  /**
   * Last known workflowId from AppState.
   * Used ONLY for change detection - compares with fresh value from AppState.
   */
  private currentWorkflowId?: number;

  /**
   * Last known versionId from AppState.
   * Used ONLY for change detection - compares with fresh value from AppState.
   */
  private currentVersionId?: number;

  private lastChunkId?: string | number;
  private chunkContext?: ExecutionContext | ChunkContext;

  constructor(
    private readonly appStateService: AppStateService
  ) {}

  /**
   * Check if chunk context has changed (step ID comparison)
   * Returns true if working with a different chunk/step
   */
  hasContextChanged(newContext: ChunkContext, previousContext?: ExecutionContext | ChunkContext): boolean {
    // Update internal reference
    const prevContext = previousContext !== undefined ? previousContext : this.chunkContext;

    // If no previous context exists or it's the exact same object reference, no change
    if (!prevContext || prevContext === newContext) {
      return false;
    }

    // Get the new chunk for comparison
    const newChunk = newContext.getChunk();

    // If new chunk is missing, consider it changed
    if (!newChunk) {
      return true;
    }

    // Compare chunk IDs to see if we're working with a different chunk
    if (this.lastChunkId !== newChunk.stepId) {
      // Update the last chunk ID
      this.lastChunkId = newChunk.stepId;

      // Update stored context
      this.chunkContext = newContext;

      return true;  // Step changed (but files stay)
    }

    return false;
  }

  /**
   * Check what aspects of the context have changed (project, workflow, version)
   * Returns an object indicating what changed and what type of reload is needed
   */
  checkContextChanges(): ContextChangeResult {
    const current = {
      projectId: this.appStateService.projectId(),
      workflowId: this.appStateService.workflowId(),
      versionId: this.appStateService.versionId()
    };

    const projectChanged = this.currentProjectId !== current.projectId;
    const workflowChanged = this.currentWorkflowId !== current.workflowId;
    const versionChanged = this.currentVersionId !== current.versionId;

    // Update tracking
    this.currentProjectId = current.projectId;
    this.currentWorkflowId = current.workflowId;
    this.currentVersionId = current.versionId;

    return {
      any: projectChanged || workflowChanged || versionChanged,
      needsFullReload: projectChanged || workflowChanged,
      needsProjectFilesReload: versionChanged && !projectChanged && !workflowChanged,
      needsWorkflowFilesReload: false  // Snapshot loads are handled via LOAD_SNAPSHOT message
    };
  }

  /**
   * Check if workflow or project has changed and trigger cleanup
   *
   * @param onCleanup Callback to execute cleanup (passed from service)
   * @param fileJournal Journal for project clearing
   * @param loadedPackages Set to clear on project switch
   * @returns true if context changed, false otherwise
   */
  async checkAndHandleContextChange(
    onCleanup: () => Promise<void>,
    fileJournal?: PyodideFileJournal,
    loadedPackages?: Set<string>
  ): Promise<boolean> {
    const currentWorkflowId = this.appStateService.workflowId();
    const currentProjectId = this.appStateService.projectId();

    // First run - save current context without cleanup
    if (this.currentWorkflowId === undefined && this.currentProjectId === undefined) {
      this.currentWorkflowId = currentWorkflowId;
      this.currentProjectId = currentProjectId;
      return false; // Don't cleanup on first run
    }

    const workflowChanged = this.currentWorkflowId !== currentWorkflowId;
    const projectChanged = this.currentProjectId !== currentProjectId;

    if (workflowChanged || projectChanged) {
      // Use enhanced environment helper for project switch
      if (this.currentProjectId && currentProjectId && this.currentProjectId !== currentProjectId) {
        // Phase 2: Simplified project switching - just clear journal
        if (fileJournal) {
          fileJournal.clearProject();
        }
        // Clear loaded packages tracking when switching projects
        if (loadedPackages) {
          loadedPackages.clear();
        }
      } else {
        // Fallback to existing cleanup for workflow-only changes
        await onCleanup();
      }

      // Update tracking
      this.currentWorkflowId = currentWorkflowId;
      this.currentProjectId = currentProjectId;

      return true;
    }

    return false;
  }

  /**
   * Get current tracked project ID
   */
  getCurrentProjectId(): number | undefined {
    return this.currentProjectId;
  }

  /**
   * Get current tracked workflow ID
   */
  getCurrentWorkflowId(): number | undefined {
    return this.currentWorkflowId;
  }

  /**
   * Get current tracked version ID
   */
  getCurrentVersionId(): number | undefined {
    return this.currentVersionId;
  }

  /**
   * Get last chunk ID
   */
  getLastChunkId(): string | number | undefined {
    return this.lastChunkId;
  }

  /**
   * Update chunk context reference
   */
  updateChunkContext(context: ChunkContext | ExecutionContext): void {
    this.chunkContext = context;
  }

  /**
   * Update last chunk ID
   */
  updateLastChunkId(chunkId: string | number): void {
    this.lastChunkId = chunkId;
  }

  /**
   * Get current chunk context
   */
  getCurrentChunkContext(): ChunkContext | ExecutionContext | undefined {
    return this.chunkContext;
  }
}
